import React from 'react';
import { colors, spacing, typography, typographyFlat } from '../../styles/tokens';

export type PolicySignalKey =
  | 'tcg_market'
  | 'tcg_low'
  | 'manapool_market'
  | 'manapool_low';

export interface PolicyPriceDebug {
  tcg_market?: number | null;
  tcg_low?: number | null;
  manapool_market?: number | null;
  manapool_low?: number | null;
  tcg_market_net?: number | null;
  tcg_low_net?: number | null;
  winning_source?: PolicySignalKey | string | null;
  high?: number | null;
  high_raw?: number | null;
  outlier_rejected?: number | null;
  /** TCG-only peer dampen (present when policy debug was stamped with peer-dampen). */
  peer_dampen_alpha?: number | null;
  high_undamped?: number | null;
  peer_signal?: number | null;
  peer_signal_source?: string | null;
  peer_dampen_applied?: boolean | null;
  low?: number | null;
  blend?: string | null;
  age_days?: number | null;
  policy_price?: number | null;
  /** Condition-store join provenance for Live Preview blanks. */
  tcg_export_status?: string | null;
  manapool_low_status?: string | null;
  tcg_export_stale_market?: number | null;
  tcg_export_stale_low?: number | null;
  manapool_low_stale?: number | null;
}

export const POLICY_SIGNAL_META: {
  key: PolicySignalKey;
  short: string;
  label: string;
}[] = [
  { key: 'tcg_market', short: 'TCG M', label: 'TCG Market' },
  { key: 'tcg_low', short: 'TCG L', label: 'TCG Low' },
  { key: 'manapool_market', short: 'MP M', label: 'Manapool Market' },
  { key: 'manapool_low', short: 'MP L', label: 'Manapool Low' },
];

interface PolicySignalsPanelProps {
  policyPrice?: number | null;
  debug?: PolicyPriceDebug | null;
  /** Compact strip for preview table rows */
  compact?: boolean;
}

function money(value?: number | null): string {
  return value != null && Number.isFinite(Number(value)) ? `$${Number(value).toFixed(2)}` : '—';
}

function asMoney(value?: number | null): number | null {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Math.round(Number(value) * 100) / 100;
}

function ageLabel(age?: number | null): string {
  if (age == null || !Number.isFinite(Number(age))) {
    return 'new (100% high)';
  }
  return `${Math.round(Number(age))}d`;
}

function isSignalKey(value: unknown): value is PolicySignalKey {
  return (
    value === 'tcg_market' ||
    value === 'tcg_low' ||
    value === 'manapool_market' ||
    value === 'manapool_low'
  );
}

/** List dollars used for soft-high comparison (raw marketplace list, not fee-net). */
function listAmountForKey(key: PolicySignalKey, blob: PolicyPriceDebug): number | null {
  return asMoney(blob[key]);
}

function formatSignalDisplay(key: PolicySignalKey, blob: PolicyPriceDebug): string {
  const raw = money(blob[key]);
  // Net annotation is fee reference only — not the win/basis unit.
  if (key === 'tcg_market' && blob.tcg_market_net != null) {
    return `${raw} → net ${money(blob.tcg_market_net)} (fee ref)`;
  }
  if (key === 'tcg_low' && blob.tcg_low_net != null) {
    return `${raw} → net ${money(blob.tcg_low_net)} (fee ref)`;
  }
  return raw;
}

/** Soft-high line: damped high, with undamped annotation when peer-dampen applied. */
function formatHighLowDisplay(blob: PolicyPriceDebug): string {
  const highPart =
    blob.high_undamped != null &&
    asMoney(blob.high) != null &&
    asMoney(blob.high_undamped) != null &&
    asMoney(blob.high) !== asMoney(blob.high_undamped)
      ? `${money(blob.high)} (undamped ${money(blob.high_undamped)})`
      : money(blob.high);
  let line = `${highPart} / ${money(blob.low)}`;
  if (blob.winning_source) {
    line += ` · win ${String(blob.winning_source)}`;
  }
  if (blob.outlier_rejected != null) {
    line += ` · dropped ${money(blob.outlier_rejected)}`;
  }
  if (blob.peer_dampen_applied && blob.peer_signal != null) {
    const peerSrc = blob.peer_signal_source ? ` ${blob.peer_signal_source}` : '';
    const alpha =
      blob.peer_dampen_alpha != null && Number.isFinite(Number(blob.peer_dampen_alpha))
        ? ` α=${Number(blob.peer_dampen_alpha)}`
        : '';
    line += ` · peer${peerSrc} ${money(blob.peer_signal)}${alpha}`;
  }
  return line;
}

/** Which of the four signals equal the computed high / low (price basis). */
export function signalBasisRoles(debug?: PolicyPriceDebug | null): {
  highKeys: PolicySignalKey[];
  lowKeys: PolicySignalKey[];
  usesHigh: boolean;
  usesLow: boolean;
} {
  const blob = debug || {};
  const high = asMoney(blob.high);
  const low = asMoney(blob.low);
  const blend = String(blob.blend || '');
  const usesLow =
    Boolean(low != null) &&
    blend !== 'high' &&
    blend !== 'high_no_low' &&
    blend !== 'none' &&
    blend !== '';
  const usesHigh = blend !== 'none' && blend !== '' && high != null;

  const highKeys: PolicySignalKey[] = [];
  if (usesHigh && isSignalKey(blob.winning_source)) {
    highKeys.push(blob.winning_source);
  } else if (usesHigh && high != null) {
    for (const { key } of POLICY_SIGNAL_META) {
      const value = listAmountForKey(key, blob);
      if (value != null && value === high) highKeys.push(key);
    }
  }

  const lowKeys: PolicySignalKey[] = [];
  if (usesLow && low != null) {
    for (const key of ['tcg_low', 'manapool_low'] as PolicySignalKey[]) {
      const value = listAmountForKey(key, blob);
      if (value != null && value === low) lowKeys.push(key);
    }
  }
  return { highKeys, lowKeys, usesHigh, usesLow };
}

function signalRole(
  key: PolicySignalKey,
  roles: ReturnType<typeof signalBasisRoles>,
): 'high' | 'low' | null {
  if (roles.highKeys.includes(key)) return 'high';
  if (roles.lowKeys.includes(key)) return 'low';
  return null;
}

export function PolicySignalsPanel({
  policyPrice,
  debug,
  compact = false,
}: PolicySignalsPanelProps) {
  const price = policyPrice ?? debug?.policy_price ?? null;
  const blob = debug || {};
  const roles = signalBasisRoles(blob);

  if (compact) {
    return (
      <div style={styles.compactWrap} title={blendTitle(blob, roles)}>
        <div style={styles.compactSignals}>
          {POLICY_SIGNAL_META.map(({ key, short }) => {
            const role = signalRole(key, roles);
            const value = blob[key];
            const netHint =
              key === 'tcg_market' && blob.tcg_market_net != null
                ? ` net ${money(blob.tcg_market_net)}`
                : key === 'tcg_low' && blob.tcg_low_net != null
                  ? ` net ${money(blob.tcg_low_net)}`
                  : '';
            return (
              <span
                key={key}
                style={{
                  ...styles.compactChip,
                  ...(role === 'high' ? styles.compactChipHigh : {}),
                  ...(role === 'low' ? styles.compactChipLow : {}),
                  ...(value == null ? styles.compactChipEmpty : {}),
                }}
                title={formatSignalDisplay(key, blob)}
              >
                <span style={styles.compactKey}>{short}</span>
                {money(value)}
                {netHint ? (
                  <span style={styles.compactNet}>{netHint}</span>
                ) : null}
                {role === 'high' ? ' ★' : role === 'low' ? ' ↓' : ''}
              </span>
            );
          })}
        </div>
        <div style={styles.compactMeta}>
          {ageLabel(blob.age_days)}
          {blob.blend ? ` · ${blob.blend}` : ''}
          {blob.winning_source ? ` · win ${String(blob.winning_source)}` : ''}
          {blob.high_undamped != null &&
          asMoney(blob.high) != null &&
          asMoney(blob.high_undamped) != null &&
          asMoney(blob.high) !== asMoney(blob.high_undamped)
            ? ` · high ${money(blob.high)} (undamped ${money(blob.high_undamped)})`
            : ''}
          {roles.highKeys.length > 0 && (
            <>
              {' · basis '}
              {roles.highKeys
                .map((k) => POLICY_SIGNAL_META.find((m) => m.key === k)?.short)
                .join('/')}
              {roles.usesLow && roles.lowKeys.length > 0
                ? ` → ${roles.lowKeys
                    .map((k) => POLICY_SIGNAL_META.find((m) => m.key === k)?.short)
                    .join('/')}`
                : ''}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.policyRow}>
        <span style={styles.policyLabel}>Policy price</span>
        <span style={styles.policyValue}>{money(price)}</span>
      </div>
      <div style={styles.grid}>
        {POLICY_SIGNAL_META.map(({ key, label }) => {
          const role = signalRole(key, roles);
          return (
            <React.Fragment key={key}>
              <span
                style={{
                  ...styles.label,
                  ...(role === 'high' ? styles.labelHigh : {}),
                  ...(role === 'low' ? styles.labelLow : {}),
                }}
              >
                {label}
                {role === 'high' ? ' (basis)' : role === 'low' ? ' (low)' : ''}
              </span>
              <span
                style={{
                  ...styles.value,
                  ...(role === 'high' ? styles.valueHigh : {}),
                  ...(role === 'low' ? styles.valueLow : {}),
                }}
              >
                {formatSignalDisplay(key, blob)}
              </span>
            </React.Fragment>
          );
        })}
        <span style={styles.label}>High / Low</span>
        <span style={styles.value}>{formatHighLowDisplay(blob)}</span>
        <span style={styles.label}>Age / blend</span>
        <span style={styles.value}>
          {ageLabel(blob.age_days)}
          {blob.blend ? ` · ${blob.blend}` : ''}
        </span>
      </div>
    </div>
  );
}

function blendTitle(
  blob: PolicyPriceDebug,
  roles: ReturnType<typeof signalBasisRoles>,
): string {
  const highLabel =
    blob.high_undamped != null &&
    asMoney(blob.high) != null &&
    asMoney(blob.high_undamped) != null &&
    asMoney(blob.high) !== asMoney(blob.high_undamped)
      ? `High ${money(blob.high)} (undamped ${money(blob.high_undamped)}) / Low ${money(blob.low)}`
      : `High ${money(blob.high)} / Low ${money(blob.low)}`;
  const parts = [highLabel, ageLabel(blob.age_days), blob.blend || ''];
  if (blob.outlier_rejected != null) {
    parts.push(`dropped outlier ${money(blob.outlier_rejected)}`);
  }
  if (blob.winning_source) {
    parts.push(`win ${String(blob.winning_source)}`);
  }
  if (blob.peer_dampen_applied && blob.peer_signal != null) {
    parts.push(`peer ${money(blob.peer_signal)}`);
  }
  if (roles.highKeys.length) {
    parts.push(
      `basis ${roles.highKeys
        .map((k) => POLICY_SIGNAL_META.find((m) => m.key === k)?.label)
        .join(', ')}`,
    );
  }
  if (roles.usesLow && roles.lowKeys.length) {
    parts.push(
      `blending toward ${roles.lowKeys
        .map((k) => POLICY_SIGNAL_META.find((m) => m.key === k)?.label)
        .join(', ')}`,
    );
  }
  return parts.filter(Boolean).join(' · ');
}

const styles = {
  wrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  policyRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: spacing.sm,
  },
  policyLabel: {
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.text,
  },
  policyValue: {
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.primary,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: spacing.md,
    rowGap: spacing.xs,
  },
  label: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  labelHigh: {
    color: colors.primaryDark,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  labelLow: {
    color: colors.secondaryDark,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  value: {
    fontSize: typography.small.fontSize,
    color: colors.text,
    textAlign: 'right' as const,
  },
  valueHigh: {
    color: colors.primaryDark,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  valueLow: {
    color: colors.secondaryDark,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  compactWrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    marginTop: spacing.xs,
  },
  compactSignals: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '4px',
  },
  compactChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: typographyFlat.fontSize.xs,
    backgroundColor: colors.gray100,
    color: colors.textSecondary,
    border: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap' as const,
  },
  compactChipHigh: {
    backgroundColor: `${colors.primary}18`,
    color: colors.primaryDark,
    borderColor: colors.primaryLight,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  compactChipLow: {
    backgroundColor: `${colors.secondary}18`,
    color: colors.secondaryDark,
    borderColor: colors.secondaryLight,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  compactChipEmpty: {
    opacity: 0.55,
  },
  compactKey: {
    fontWeight: typographyFlat.fontWeight.medium,
  },
  compactNet: {
    opacity: 0.85,
    fontSize: typographyFlat.fontSize.xs,
  },
  compactMeta: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
  },
};
